/**
 * Shared expense-FE bits (ADR-0020 Phase 4): the receipt panel (list + download,
 * optionally upload/delete when the claim is still editable), the lifecycle
 * status pill, and the reimbursement-state label.
 */
import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listReceipts,
  uploadReceipt,
  downloadReceipt,
  deleteReceipt,
  RECEIPT_ACCEPT,
  RECEIPT_ALLOWED_LABEL,
  type ExpenseClaim,
  type ExpenseClaimStatus,
} from '../api/expense.js';
import { ApiError } from '../api/client.js';
import { Alert } from '../components/Alert.js';
import { formatBytes } from '../lib/format.js';

/** A claim is editable (receipts can be attached/removed) only while draft or
 *  pending — once approved/terminal the signed-off artifact must not change. */
export function isEditable(status: ExpenseClaimStatus): boolean {
  return status === 'draft' || status === 'pending_manager' || status === 'pending_finance';
}

const STATUS_STYLES: Record<string, string> = {
  draft: 'bg-gray-900 text-gray-400 border-gray-800',
  pending_manager: 'bg-yellow-950 text-yellow-400 border-yellow-900',
  pending_finance: 'bg-yellow-950 text-yellow-400 border-yellow-900',
  approved: 'bg-blue-950 text-blue-400 border-blue-900',
  reimbursed: 'bg-green-950 text-green-400 border-green-900',
  paid_offcycle: 'bg-green-950 text-green-400 border-green-900',
  rejected: 'bg-red-950 text-red-400 border-red-900',
  cancelled: 'bg-gray-900 text-gray-500 border-gray-800',
};

export function StatusPill({ status }: { status: ExpenseClaimStatus }): JSX.Element {
  return (
    <span className={`text-xs px-2 py-0.5 rounded border ${STATUS_STYLES[status] ?? 'border-gray-800'}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

/** Human-readable reimbursement state for the my-claims / portfolio rows. */
export function reimbursementLabel(claim: ExpenseClaim): string | null {
  if (claim.status === 'reimbursed') return 'Reimbursed via payroll';
  if (claim.status === 'paid_offcycle') {
    return `Paid off-cycle${claim.offcyclePaidMethod ? ` · ${claim.offcyclePaidMethod}` : ''}${
      claim.offcyclePaidReference ? ` · ${claim.offcyclePaidReference}` : ''
    }`;
  }
  if (claim.status === 'approved') return 'Awaiting reimbursement';
  return null;
}

/**
 * Receipts for a claim: always lists + downloads; when `canModify` the employee
 * may attach (allow-list shown, graceful 415) or remove receipts. `canModify`
 * should be true only for the claimant while the claim is still editable.
 */
export function ClaimReceipts({ claimId, canModify }: { claimId: string; canModify: boolean }): JSX.Element {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['expense', 'receipts', claimId],
    queryFn: () => listReceipts(claimId),
  });
  const invalidate = (): Promise<void> =>
    queryClient.invalidateQueries({ queryKey: ['expense', 'receipts', claimId] });

  const upload = useMutation({
    mutationFn: (f: File) => uploadReceipt(claimId, f),
    onSuccess: async () => {
      setError(null);
      if (fileRef.current) fileRef.current.value = '';
      await invalidate();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 415) {
        setError(`That file type isn’t accepted. Allowed: ${RECEIPT_ALLOWED_LABEL}.`);
      } else {
        setError(e instanceof ApiError ? e.message : 'Upload failed.');
      }
    },
  });
  const remove = useMutation({
    mutationFn: (artifactId: string) => deleteReceipt(claimId, artifactId),
    onSuccess: () => void invalidate(),
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Delete failed.'),
  });

  const receipts = query.data?.receipts ?? [];

  return (
    <div className="space-y-2">
      <p className="text-[0.65rem] uppercase tracking-wide text-gray-500">Receipts</p>
      {query.isLoading ? (
        <p className="text-xs text-gray-500">Loading…</p>
      ) : receipts.length === 0 ? (
        <p className="text-xs text-gray-600">No receipt attached.</p>
      ) : (
        <ul className="space-y-1">
          {receipts.map((r) => (
            <li key={r.id} className="flex items-center gap-3 text-xs">
              <span className="text-gray-300">{r.filename}</span>
              <span className="text-gray-600">{formatBytes(r.sizeBytes)}</span>
              <button
                type="button"
                onClick={() => void downloadReceipt(claimId, r.id, r.filename)}
                className="text-gray-300 underline"
              >
                Download
              </button>
              {canModify ? (
                <button
                  type="button"
                  onClick={() => remove.mutate(r.id)}
                  disabled={remove.isPending}
                  className="text-red-400 underline disabled:opacity-50"
                >
                  Remove
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {canModify ? (
        <div className="space-y-1">
          <input
            ref={fileRef}
            type="file"
            accept={RECEIPT_ACCEPT}
            aria-label="Upload receipt"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) upload.mutate(f);
            }}
            disabled={upload.isPending}
            className="block text-xs text-gray-300"
          />
          <p className="text-[0.65rem] text-gray-600">Accepted: {RECEIPT_ALLOWED_LABEL}. Max 10 MB.</p>
        </div>
      ) : null}

      {error ? <Alert variant="error" compact className="mt-1">{error}</Alert> : null}
    </div>
  );
}
